import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { AdhocInviteBanner } from "@/components/AdhocInviteBanner";
import { RecentAdhocMeetings } from "@/components/RecentAdhocMeetings";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format, setHours, setMinutes, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  CalendarIcon,
  Clock,
  Plus,
  Play,
  Pause,
  CheckCircle2,
  Users,
  Activity,
  Loader2,
  Settings,
  MapPin,
  X,
  Video,
  FileText,
  Pencil,
  Trash2,
  Wifi,
  WifiOff,
  Bell,
  BellRing,
  Volume2,
  VolumeX,
  History,
  Mail,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationSettings from "@/components/NotificationSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// All therapists provide all visit types — no per-therapist filtering.
const ALL_VISIT_TYPES = ["evaluation", "supervisory", "st_evaluation", "st_treatment"];

// Isolated Profile Settings Dialog Component - prevents parent re-renders from affecting form state
const ProfileSettingsDialog = ({ profile, onSave, externalOpen, onOpenChange: externalOnOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localForm, setLocalForm] = useState({
    specialty: "",
    states_covered: [],
    max_concurrent: 1,
    phone: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // Sync with external open state if provided
  useEffect(() => {
    if (externalOpen !== undefined) {
      if (externalOpen && profile) {
        // Initialize form with current profile data when opening externally
        setLocalForm({
          specialty: profile.specialty || "",
          states_covered: profile.states_covered || [],
          max_concurrent: profile.max_concurrent || 1,
          phone: profile.phone || "",
        });
      }
      setIsOpen(externalOpen);
    }
  }, [externalOpen, profile]);

  // Only initialize form when dialog opens
  const handleOpenChange = (open) => {
    if (open && profile) {
      // Initialize form with current profile data when opening
      setLocalForm({
        specialty: profile.specialty || "",
        states_covered: profile.states_covered || [],
        max_concurrent: profile.max_concurrent || 1,
        phone: profile.phone || "",
      });
    }
    setIsOpen(open);
    if (externalOnOpenChange) {
      externalOnOpenChange(open);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/therapists/me`, localForm);
      toast.success("Profile updated!");
      setIsOpen(false);
      if (externalOnOpenChange) externalOnOpenChange(false);
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleState = (state) => {
    setLocalForm(prev => {
      const currentStates = prev.states_covered;
      if (currentStates.includes(state)) {
        return { ...prev, states_covered: currentStates.filter(s => s !== state) };
      } else {
        return { ...prev, states_covered: [...currentStates, state] };
      }
    });
  };

  const removeState = (state) => {
    setLocalForm(prev => ({
      ...prev,
      states_covered: prev.states_covered.filter(s => s !== state)
    }));
  };

  // If externally controlled, don't render a trigger button
  const isExternallyControlled = externalOpen !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {!isExternallyControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2" data-testid="profile-settings-btn">
            <Settings className="w-4 h-4" />
            License Settings
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>License & Profile Settings</DialogTitle>
          <DialogDescription>
            Configure your licensed states and specialties
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="therapist-phone">Contact phone</Label>
            <Input
              id="therapist-phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={localForm.phone}
              onChange={(e) => setLocalForm(prev => ({ ...prev, phone: e.target.value }))}
              data-testid="therapist-phone-input"
            />
            <p className="text-xs text-muted-foreground">
              Shown to facilitators on the appointment and in the booking confirmation email.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Specialty</Label>
            <Select
              value={localForm.specialty}
              onValueChange={(value) => setLocalForm(prev => ({ ...prev, specialty: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select specialty" />
              </SelectTrigger>
              <SelectContent>
                {["PT", "OT", "SLP"].map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "PT" ? "Physical Therapy (PT)" : s === "OT" ? "Occupational Therapy (OT)" : "Speech Language Pathology (SLP)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Licensed States ({localForm.states_covered.length} selected)</Label>
            <div className="flex flex-wrap gap-1 mb-2">
              {localForm.states_covered.map((state) => (
                <Badge key={state} variant="secondary" className="gap-1">
                  {state}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => removeState(state)}
                  />
                </Badge>
              ))}
            </div>
            <ScrollArea className="h-48 border rounded-md p-2">
              <div className="grid grid-cols-2 gap-2">
                {ALL_STATES.map((state) => (
                  <div key={state} className="flex items-center space-x-2">
                    <Checkbox
                      id={`profile-state-${state}`}
                      checked={localForm.states_covered.includes(state)}
                      onCheckedChange={() => toggleState(state)}
                    />
                    <label
                      htmlFor={`profile-state-${state}`}
                      className="text-sm cursor-pointer"
                    >
                      {state}
                    </label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          <Button onClick={handleSave} className="w-full" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Profile"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const blockStatusColors = {
  scheduled: "bg-blue-100 text-blue-700 border-blue-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  paused: "bg-amber-100 text-amber-700 border-amber-200",
  released: "bg-slate-100 text-slate-700 border-slate-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
};

const TherapistDashboard = () => {
  const { user, token } = useAuth();
  const { isConnected, connectionFailed, lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [profileSettingsOpen, setProfileSettingsOpen] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  
  // Check if user has specific roles (multi-role support)
  const hasRole = (role) => user?.roles?.includes(role);
  const isAdmin = hasRole("admin");
  const isTherapist = hasRole("therapist");
  // Admin-only means has admin but NOT therapist role
  const isAdminOnly = isAdmin && !isTherapist;
  // Can manage blocks if therapist role is present
  const canManageBlocks = isTherapist;

  // New block form - states and specialty will be set from profile
  const [newBlock, setNewBlock] = useState({
    date: null,
    extraDates: [],   // Req #6: additional dates to duplicate this block onto
    startTime: "09:00",
    endTime: "17:00",
  });

  // Queue alert state
  const [showQueueAlert, setShowQueueAlert] = useState(false);
  const [alertMuted, setAlertMuted] = useState(false);
  const [startingSessionFromBanner, setStartingSessionFromBanner] = useState(false);
  const [activeView, setActiveView] = useState("blocks"); // Changed from activeTab
  const lastAlertedQueueLengthRef = useRef(0);
  const audioRef = useRef(null);
  const alertIntervalRef = useRef(null);

  // Req #7: Cancel appointment dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingAppt, setCancelingAppt] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // Req #8: Ad-hoc meeting state
  const [adhocDialogOpen, setAdhocDialogOpen] = useState(false);
  const [invitableUsers, setInvitableUsers] = useState([]);
  const [adhocInviteeId, setAdhocInviteeId] = useState("");
  const [adhocTopic, setAdhocTopic] = useState("");
  const [adhocSubmitting, setAdhocSubmitting] = useState(false);

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ availability_blocks: [], appointments: [], sessions: [] });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [blocksRes, sessionsRes, queueRes, profileRes] = await Promise.all([
        axios.get(`${API}/availability-blocks/my`),
        axios.get(`${API}/sessions`),
        axios.get(`${API}/queue`).catch(() => ({ data: { queue: [] } })),
        axios.get(`${API}/therapists/me`).catch(() => ({ data: null })),
      ]);
      setBlocks(blocksRes.data.blocks);
      setSessions(sessionsRes.data.sessions);
      setQueue(queueRes.data.queue);
      if (profileRes.data) {
        setProfile(profileRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch calendar data when month changes
  const fetchCalendarData = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const month = calendarMonth.getMonth() + 1;
      const year = calendarMonth.getFullYear();
      const response = await axios.get(`${API}/therapists/${profile.id}/calendar?month=${month}&year=${year}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
    }
  }, [profile?.id, calendarMonth]);

  useEffect(() => {
    if (profile?.id) {
      fetchCalendarData();
    }
  }, [profile?.id, fetchCalendarData]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - only run once on mount

  // Listen for WebSocket events and refresh data
  useEffect(() => {
    if (!lastMessage) return;
    
    // Refresh data on relevant events
    if (["patient_ready", "session_started", "patient_checked_in", "patient_claimed", "queue_updated"].includes(lastMessage.type)) {
      console.log("WebSocket event received, refreshing data:", lastMessage.type);
      fetchData();
      
      // If a patient was claimed (by any therapist), dismiss the alert for this patient
      if (lastMessage.type === "patient_claimed") {
        setShowQueueAlert(false);
        // Reset the alert tracking since the queue changed
        lastAlertedQueueLengthRef.current = Math.max(0, lastAlertedQueueLengthRef.current - 1);
      }
    }
  }, [lastMessage, fetchData]);

  // Fallback polling if WebSocket disconnects - poll every 15 seconds
  useEffect(() => {
    if (isConnected) return;
    
    console.log("WebSocket not connected, falling back to polling");
    const interval = setInterval(() => {
      console.log("Polling for queue updates...");
      fetchData();
    }, 15000); // Poll every 15 seconds when WebSocket is down
    return () => clearInterval(interval);
  }, [isConnected, fetchData]);

  // Queue alert logic - show alert and play sound when:
  // 1. There are patients in the queue ASSIGNED TO THIS THERAPIST
  // 2. Therapist has NO active sessions
  // 3. Alert hasn't been shown for this queue state yet
  useEffect(() => {
    const activeSessions = sessions.filter((s) => s.outcome === "active");
    const hasActiveSession = activeSessions.length > 0;
    
    // CRITICAL FIX: Only count queue entries actually assigned to THIS therapist
    // The backend should already filter this, but we add frontend filtering as defense-in-depth
    const myQueueEntries = queue.filter((entry) => {
      // If no assigned_therapist_user_id, this entry wasn't properly assigned
      // (shouldn't happen with new booking flow, but handle gracefully)
      if (!entry.assigned_therapist_user_id) {
        console.warn("Queue entry without assigned therapist:", entry.id);
        return false;
      }
      // Only show entries assigned to the current user
      return entry.assigned_therapist_user_id === user?.id;
    });
    
    const hasQueuedPatients = myQueueEntries.length > 0;
    
    // Determine if we should show alert
    const shouldShowAlert = hasQueuedPatients && !hasActiveSession;
    
    if (shouldShowAlert && myQueueEntries.length > lastAlertedQueueLengthRef.current) {
      // New patient(s) in queue - show alert and play sound
      setShowQueueAlert(true);
      lastAlertedQueueLengthRef.current = myQueueEntries.length;
      
      // Play notification sound if not muted
      if (!alertMuted) {
        playAlertSound();
      }
    } else if (!shouldShowAlert) {
      // Hide alert (either no queue or therapist has session)
      setShowQueueAlert(false);
      // Reset alert tracking when therapist starts a session or queue empties
      if (!hasQueuedPatients) {
        lastAlertedQueueLengthRef.current = 0;
      }
    }
  }, [queue, sessions, alertMuted, user?.id]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (alertIntervalRef.current) {
        clearInterval(alertIntervalRef.current);
      }
    };
  }, []);

  // Note: Heartbeat is now handled in Layout.jsx for all pages

  // Function to play alert sound
  const playAlertSound = () => {
    try {
      // Create audio context for a notification tone
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Play a pleasant two-tone chime
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      // Play a pleasant ascending chime (C5, E5, G5)
      playTone(523.25, now, 0.15);       // C5
      playTone(659.25, now + 0.15, 0.15); // E5
      playTone(783.99, now + 0.3, 0.3);   // G5
      
      // Close audio context after sounds finish
      setTimeout(() => audioContext.close(), 1000);
    } catch (error) {
      console.log("Could not play alert sound:", error);
    }
  };

  const dismissAlert = () => {
    setShowQueueAlert(false);
  };

  const toggleMute = () => {
    setAlertMuted(!alertMuted);
    if (alertMuted) {
      toast.success("Alert sounds enabled");
    } else {
      toast.info("Alert sounds muted");
    }
  };

  const handleCreateBlock = async () => {
    if (!newBlock.date) {
      toast.error("Please select a date");
      return;
    }

    // Use profile settings for states and specialty
    if (!profile?.specialty || !profile?.states_covered?.length) {
      toast.error("Please set up your license settings (states and specialty) in your profile first");
      return;
    }

    try {
      const [startHour, startMin] = newBlock.startTime.split(":").map(Number);
      const [endHour, endMin] = newBlock.endTime.split(":").map(Number);

      // Req #6: build the full list of dates to create blocks for (primary + extras).
      const allDates = [newBlock.date, ...(newBlock.extraDates || [])];

      const payloads = allDates.map((d) => {
        const startTime = setMinutes(setHours(d, startHour), startMin);
        const endTime = setMinutes(setHours(d, endHour), endMin);
        return {
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          states_covered: profile.states_covered,
          specialty: profile.specialty,
          visit_types: ALL_VISIT_TYPES,
          max_concurrent: profile.max_concurrent || 1,
        };
      });

      const results = await Promise.allSettled(
        payloads.map((p) => axios.post(`${API}/availability-blocks`, p))
      );

      const failures = results.filter((r) => r.status === "rejected");
      const successes = results.length - failures.length;

      if (successes > 0 && failures.length === 0) {
        toast.success(`${successes} availability block${successes === 1 ? "" : "s"} created!`);
      } else if (successes > 0 && failures.length > 0) {
        toast.warning(`${successes} created, ${failures.length} failed. Check overlapping blocks.`);
      } else {
        toast.error(failures[0]?.reason?.response?.data?.detail || "Failed to create block(s)");
      }

      setDialogOpen(false);
      setNewBlock({
        date: null,
        extraDates: [],
        startTime: "09:00",
        endTime: "17:00",
      });
      fetchData();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create block");
    }
  };

  const handleBlockStatusChange = async (blockId, status) => {
    try {
      await axios.put(`${API}/availability-blocks/${blockId}/status?status=${status}`);
      toast.success(`Block ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update block");
    }
  };

  // Req #7: Therapist-initiated appointment cancellation
  const openCancelDialog = (appt) => {
    setCancelingAppt(appt);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const handleCancelAppointment = async () => {
    if (!cancelingAppt) return;
    setSubmittingCancel(true);
    try {
      await axios.put(
        `${API}/appointments/${cancelingAppt.id}/cancel`,
        null,
        { params: cancelReason.trim() ? { reason: cancelReason.trim() } : {} }
      );
      toast.success("Appointment canceled. Facilitator and patient notifications sent.");
      setCancelDialogOpen(false);
      setCancelingAppt(null);
      setCancelReason("");
      fetchData();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel appointment");
    } finally {
      setSubmittingCancel(false);
    }
  };

  // Req #8: Start an ad-hoc video meeting with another internal user
  const openAdhocDialog = async () => {
    setAdhocInviteeId("");
    setAdhocTopic("");
    setAdhocDialogOpen(true);
    if (invitableUsers.length === 0) {
      try {
        const r = await axios.get(`${API}/users/invitable`);
        setInvitableUsers(r.data?.users || []);
      } catch (e) {
        toast.error("Could not load invitable users");
      }
    }
  };

  const handleStartAdhoc = async () => {
    if (!adhocInviteeId) {
      toast.error("Please pick someone to invite");
      return;
    }
    setAdhocSubmitting(true);
    try {
      const r = await axios.post(`${API}/video/adhoc-meeting`, {
        invitee_user_id: adhocInviteeId,
        topic: adhocTopic.trim() || null,
      });
      const id = r.data?.meeting_id;
      if (id) {
        toast.success(`Inviting ${r.data?.invitee_name}…`);
        setAdhocDialogOpen(false);
        navigate(`/adhoc-meeting/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start meeting");
    } finally {
      setAdhocSubmitting(false);
    }
  };


  const handleEditBlock = (block) => {
    setEditingBlock({
      ...block,
      date: new Date(block.start_time),
      startTime: new Date(block.start_time).toTimeString().slice(0, 5),
      endTime: new Date(block.end_time).toTimeString().slice(0, 5),
      states: block.states_covered || [],
    });
    setEditDialogOpen(true);
  };

  const handleUpdateBlock = async () => {
    if (!editingBlock) return;
    
    try {
      const [startHour, startMinute] = editingBlock.startTime.split(":").map(Number);
      const [endHour, endMinute] = editingBlock.endTime.split(":").map(Number);
      
      const startTime = setMinutes(setHours(editingBlock.date, startHour), startMinute);
      const endTime = setMinutes(setHours(editingBlock.date, endHour), endMinute);
      
      await axios.put(`${API}/availability-blocks/${editingBlock.id}`, {
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        states_covered: editingBlock.states,
        specialty: editingBlock.specialty,
        visit_types: ALL_VISIT_TYPES,
        max_concurrent: editingBlock.max_concurrent,
      });
      
      toast.success("Block updated!");
      setEditDialogOpen(false);
      setEditingBlock(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update block");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm("Are you sure you want to delete this availability block?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/availability-blocks/${blockId}`);
      toast.success("Block deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete block");
    }
  };

  const handleCompleteSession = async (sessionId) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}/complete`);
      toast.success("Session completed");
      fetchData();
    } catch (error) {
      toast.error("Failed to complete session");
    }
  };

  const handleStartSession = async (queueEntryId, patientName) => {
    try {
      const res = await axios.post(`${API}/queue/${queueEntryId}/start-session`);
      toast.success(`Session started with ${patientName}`);
      fetchData();
      // Navigate to session page
      if (res.data.session_id) {
        navigate(`/session/${res.data.session_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start session");
    }
  };

  const handleStartSessionFromBanner = async () => {
    if (!queue.length) return;
    
    const firstPatient = queue[0];
    setStartingSessionFromBanner(true);
    
    try {
      const res = await axios.post(`${API}/queue/${firstPatient.id}/start-session`);
      toast.success(`Session started with ${firstPatient.patient_name}`);
      dismissAlert();
      // Navigate to session page to start video
      if (res.data.session_id) {
        navigate(`/session/${res.data.session_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start session");
    } finally {
      setStartingSessionFromBanner(false);
    }
  };

  const handleRemoveFromQueue = async (queueEntryId, patientName) => {
    if (!window.confirm(`Are you sure you want to remove ${patientName} from the queue?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/queue/${queueEntryId}`);
      toast.success(`${patientName} removed from queue`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from queue");
    }
  };

  const toggleState = (state, list, setList) => {
    if (list.includes(state)) {
      setList(list.filter((s) => s !== state));
    } else {
      setList([...list, state]);
    }
  };

  const activeBlocks = blocks.filter((b) => b.status === "active");
  const scheduledBlocks = blocks.filter((b) => b.status === "scheduled");
  const activeSessions = sessions
    .filter((s) => s.outcome === "active")
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time)); // Newest first
  
  // Filter completed sessions to only show those completed TODAY
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  
  const completedSessions = sessions
    .filter((s) => {
      if (s.outcome !== "completed") return false;
      // Check if the session was completed today (using end_time or start_time)
      const sessionDate = new Date(s.end_time || s.start_time);
      return sessionDate >= todayStart && sessionDate < todayEnd;
    })
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time)); // Newest first

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Short format for calendar display (e.g., "9-5" or "9a-5p")
  const formatTimeRange = (startStr, endStr) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const startHour = start.getHours();
    const endHour = end.getHours();
    const startMin = start.getMinutes();
    const endMin = end.getMinutes();
    
    // Format as "9-5" or "9:30-5:30"
    const formatShort = (h, m) => {
      if (m === 0) return h > 12 ? h - 12 : (h === 0 ? 12 : h);
      return `${h > 12 ? h - 12 : (h === 0 ? 12 : h)}:${m.toString().padStart(2, '0')}`;
    };
    
    return `${formatShort(startHour, startMin)}-${formatShort(endHour, endMin)}`;
  };

  // Calculate session duration in minutes
  const getSessionDuration = (session) => {
    if (session.duration_minutes) return session.duration_minutes;
    if (session.start_time && session.end_time) {
      const start = new Date(session.start_time);
      const end = new Date(session.end_time);
      return Math.round((end - start) / (1000 * 60));
    }
    return null;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Active Status Bar - Shows when therapist has active availability blocks */}
      {activeBlocks.length > 0 && canManageBlocks && (
        <div 
          className="fixed top-0 left-0 right-0 z-40 bg-emerald-600 text-white py-2 px-6 shadow-sm"
          data-testid="active-status-bar"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="font-medium text-sm">
                You are ACTIVE and available for sessions
              </span>
            </div>
            <span className="text-emerald-200 text-sm">
              {activeBlocks.length} block{activeBlocks.length > 1 ? 's' : ''} active
            </span>
          </div>
        </div>
      )}

      {/* Queue Alert Banner - Shows when patients are waiting and therapist is not in session */}
      {showQueueAlert && (
        <div 
          className={cn(
            "fixed left-0 right-0 z-50 animate-pulse",
            activeBlocks.length > 0 && canManageBlocks ? "top-10" : "top-0"
          )}
          data-testid="queue-alert-banner"
        >
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-4 px-6 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div 
                className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => {
                  dismissAlert();
                  setActiveView("queue");
                  // Scroll to queue section after a brief delay for view switch
                  setTimeout(() => {
                    document.getElementById("patient-queue-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 100);
                }}
              >
                <div className="relative">
                  <BellRing className="w-8 h-8 animate-bounce" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-xs font-bold">
                    {queue.length}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-lg">
                    {queue.length === 1 
                      ? "1 Patient Waiting!" 
                      : `${queue.length} Patients Waiting!`}
                  </p>
                  <p className="text-sm text-white/90">
                    <span className="font-semibold">{queue[0]?.patient_name}</span> is ready for their session
                    {queue[0]?.discipline && <span className="ml-1">({queue[0].discipline})</span>}
                    {queue.length > 1 && <span className="ml-1 opacity-75">+{queue.length - 1} more</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* VIEW QUEUE BUTTON - Primary Action - Takes therapist to queue to confirm before starting */}
                <Button
                  size="sm"
                  onClick={() => {
                    dismissAlert();
                    setActiveView("queue");
                    setTimeout(() => {
                      document.getElementById("patient-queue-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 100);
                  }}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 shadow-md"
                  data-testid="view-queue-from-banner"
                >
                  <Users className="w-4 h-4" />
                  View Queue & Start Session
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={toggleMute}
                  className="gap-2"
                  data-testid="toggle-alert-sound"
                >
                  {alertMuted ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    dismissAlert();
                    setActiveView("queue");
                  }}
                  className="gap-2"
                  data-testid="view-queue-btn"
                >
                  <Users className="w-4 h-4" />
                  View All
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={dismissAlert}
                  className="text-white hover:bg-white/20"
                  data-testid="dismiss-alert"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div data-testid="therapist-dashboard" className={cn(
        "space-y-8", 
        showQueueAlert && activeBlocks.length > 0 && canManageBlocks && "pt-28",
        showQueueAlert && !(activeBlocks.length > 0 && canManageBlocks) && "pt-20",
        !showQueueAlert && activeBlocks.length > 0 && canManageBlocks && "pt-10"
      )}>
        {/* Persistent incoming ad-hoc call invitations — polls the
            backend so a missed WS message doesn't lose the invite. */}
        <AdhocInviteBanner />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                {isAdminOnly ? "Therapist View" : "Therapist Dashboard"}
              </h1>
              {isAdminOnly && (
                <Badge className="bg-purple-100 text-purple-700">
                  Admin Viewing
                </Badge>
              )}
              {/* WebSocket connection indicator - only show if connected or actively trying */}
              {isConnected ? (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700" data-testid="ws-status">
                  <Wifi className="w-3 h-3" />
                  <span>Live</span>
                </div>
              ) : connectionFailed ? (
                /* WebSocket not available - using polling fallback, show subtle indicator */
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600" data-testid="ws-status">
                  <Activity className="w-3 h-3" />
                  <span>Polling</span>
                </div>
              ) : null /* Don't show anything during initial connection attempts */}
            </div>
            <p className="text-muted-foreground mt-1">
              {isAdminOnly ? "View therapist queue and active sessions" : "Manage your availability and sessions"}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Notification Settings Button */}
            <Button 
              variant={showNotificationSettings ? "default" : "outline"}
              size="icon"
              onClick={() => setShowNotificationSettings(!showNotificationSettings)}
              data-testid="notification-settings-btn"
              title="Notification Settings"
            >
              <Bell className="w-4 h-4" />
            </Button>
            
            {/* Profile Settings Button - only for therapists and admin_therapist */}
            {canManageBlocks && (
              <Button 
                variant="outline" 
                className="gap-2" 
                onClick={() => setProfileSettingsOpen(true)}
                data-testid="profile-settings-btn"
              >
                <Settings className="w-4 h-4" />
                License Settings
              </Button>
            )}

            {/* Req #8: Ad-hoc meeting trigger */}
            <Button
              variant="outline"
              className="gap-2"
              onClick={openAdhocDialog}
              data-testid="start-adhoc-meeting-btn"
              title="Start an ad-hoc video meeting with a facilitator or admin"
            >
              <Video className="w-4 h-4" />
              Ad-Hoc Meeting
            </Button>

            {/* Add Availability Block Button - only for therapists and admin_therapist */}
            {canManageBlocks && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-availability-btn">
                  <Plus className="w-4 h-4" />
                  Add Availability Block
                </Button>
              </DialogTrigger>              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Availability Block</DialogTitle>
                  <DialogDescription>
                    Set your available time for patient sessions
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !newBlock.date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newBlock.date ? format(newBlock.date, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 z-[200]"
                        align="start"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <Calendar
                          mode="single"
                          selected={newBlock.date}
                          onSelect={(date) => setNewBlock({ ...newBlock, date })}
                          fromDate={startOfDay(new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Req #6: Repeat this block on additional days */}
                  <div className="space-y-2">
                    <Label>Repeat on additional days <span className="text-xs text-muted-foreground">(optional)</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="repeat-dates-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newBlock.extraDates?.length
                            ? `${newBlock.extraDates.length} additional day${newBlock.extraDates.length === 1 ? "" : "s"} selected`
                            : "Pick more dates (same time window)"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 z-[200]"
                        align="start"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <Calendar
                          mode="multiple"
                          selected={newBlock.extraDates || []}
                          onSelect={(dates) =>
                            setNewBlock({
                              ...newBlock,
                              // Exclude the primary date so we don't create duplicates on it.
                              extraDates: (dates || []).filter(
                                (d) => !newBlock.date || format(d, "yyyy-MM-dd") !== format(newBlock.date, "yyyy-MM-dd")
                              ),
                            })
                          }
                          fromDate={startOfDay(new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                    {newBlock.extraDates?.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Will create blocks on: {[newBlock.date, ...newBlock.extraDates].filter(Boolean).map(d => format(d, "MMM d")).join(", ")}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Select
                        value={newBlock.startTime}
                        onValueChange={(value) => setNewBlock({ ...newBlock, startTime: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[200] max-h-[300px]">
                          {(() => {
                            const times = [];
                            for (let h = 0; h < 24; h++) {
                              for (let m of [0, 30]) {
                                const hour24 = h.toString().padStart(2, '0');
                                const value = `${hour24}:${m.toString().padStart(2, '0')}`;
                                const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                const ampm = h < 12 ? 'AM' : 'PM';
                                const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                times.push({ value, label });
                              }
                            }
                            return times.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Select
                        value={newBlock.endTime}
                        onValueChange={(value) => setNewBlock({ ...newBlock, endTime: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[200] max-h-[300px]">
                          {(() => {
                            const times = [];
                            for (let h = 0; h < 24; h++) {
                              for (let m of [0, 30]) {
                                const hour24 = h.toString().padStart(2, '0');
                                const value = `${hour24}:${m.toString().padStart(2, '0')}`;
                                const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                const ampm = h < 12 ? 'AM' : 'PM';
                                const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                times.push({ value, label });
                              }
                            }
                            return times.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Show profile settings that will be used */}
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <p className="text-sm font-medium">Using your License Settings:</p>
                    <div className="text-sm text-muted-foreground">
                      <p><strong>Specialty:</strong> {profile?.specialty || "Not set"}</p>
                      <p><strong>States:</strong> {profile?.states_covered?.join(", ") || "Not set"}</p>
                    </div>
                    {(!profile?.specialty || !profile?.states_covered?.length) && (
                      <p className="text-xs text-destructive">
                        ⚠️ Please set up your License Settings before creating blocks
                      </p>
                    )}
                  </div>

                  <Button onClick={handleCreateBlock} className="w-full" disabled={!profile?.specialty || !profile?.states_covered?.length}>
                    Create Block
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}

            {/* Edit Availability Block Dialog - only for therapists and admin_therapist */}
            {canManageBlocks && (
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Availability Block</DialogTitle>
                  <DialogDescription>
                    Update your availability block settings
                  </DialogDescription>
                </DialogHeader>
                {editingBlock && (
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !editingBlock.date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editingBlock.date ? format(editingBlock.date, "PPP") : "Select a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 z-[200]"
                          align="start"
                          onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                          <Calendar
                            mode="single"
                            selected={editingBlock.date}
                            onSelect={(date) => setEditingBlock({ ...editingBlock, date })}
                            fromDate={startOfDay(new Date())}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Select
                          value={editingBlock.startTime}
                          onValueChange={(val) => setEditingBlock({ ...editingBlock, startTime: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[200] max-h-[300px]">
                            {(() => {
                              const times = [];
                              for (let h = 0; h < 24; h++) {
                                for (let m of [0, 30]) {
                                  const hour24 = h.toString().padStart(2, '0');
                                  const value = `${hour24}:${m.toString().padStart(2, '0')}`;
                                  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                  const ampm = h < 12 ? 'AM' : 'PM';
                                  const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                  times.push({ value, label });
                                }
                              }
                              return times.map((t) => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Select
                          value={editingBlock.endTime}
                          onValueChange={(val) => setEditingBlock({ ...editingBlock, endTime: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[200] max-h-[300px]">
                            {(() => {
                              const times = [];
                              for (let h = 0; h < 24; h++) {
                                for (let m of [0, 30]) {
                                  const hour24 = h.toString().padStart(2, '0');
                                  const value = `${hour24}:${m.toString().padStart(2, '0')}`;
                                  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                  const ampm = h < 12 ? 'AM' : 'PM';
                                  const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                  times.push({ value, label });
                                }
                              }
                              return times.map((t) => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Specialty</Label>
                      <Select
                        value={editingBlock.specialty}
                        onValueChange={(val) => setEditingBlock({ ...editingBlock, specialty: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select specialty" />
                        </SelectTrigger>
                        <SelectContent className="z-[200]">
                          <SelectItem value="PT">Physical Therapy (PT)</SelectItem>
                          <SelectItem value="OT">Occupational Therapy (OT)</SelectItem>
                          <SelectItem value="SLP">Speech Language Pathology (SLP)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>States for this Block ({editingBlock.states?.length || 0} selected)</Label>
                      <p className="text-xs text-muted-foreground">Select all states this block should cover (not limited to your profile states)</p>
                      <ScrollArea className="h-32 border rounded-md p-2">
                        <div className="grid grid-cols-2 gap-2">
                          {ALL_STATES.map((state) => (
                            <div key={state} className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-block-state-${state}`}
                                checked={editingBlock.states?.includes(state)}
                                onCheckedChange={() => {
                                  const states = editingBlock.states || [];
                                  if (states.includes(state)) {
                                    setEditingBlock({
                                      ...editingBlock,
                                      states: states.filter((s) => s !== state)
                                    });
                                  } else {
                                    setEditingBlock({
                                      ...editingBlock,
                                      states: [...states, state]
                                    });
                                  }
                                }}
                              />
                              <label htmlFor={`edit-block-state-${state}`} className="text-sm cursor-pointer">
                                {state}
                              </label>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateBlock} className="flex-1">
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            )}

            {/* Req #7: Cancel appointment dialog (therapist-initiated) */}
            <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
              <DialogContent className="max-w-md" data-testid="cancel-appointment-dialog">
                <DialogHeader>
                  <DialogTitle>Cancel Appointment</DialogTitle>
                  <DialogDescription>
                    {cancelingAppt && (
                      <>
                        Cancel <strong>{cancelingAppt.patient_name}</strong>&apos;s appointment on{" "}
                        <strong>{new Date(cancelingAppt.window_start).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</strong>?
                        The facilitator and patient will be notified by email.
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="cancel-reason">Reason (optional, sent in the email)</Label>
                    <Textarea
                      id="cancel-reason"
                      data-testid="cancel-reason-input"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="e.g. Therapist illness — patient will be rescheduled."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setCancelDialogOpen(false)}
                      className="flex-1"
                      disabled={submittingCancel}
                    >
                      Keep Appointment
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleCancelAppointment}
                      disabled={submittingCancel}
                      className="flex-1"
                      data-testid="confirm-cancel-appointment"
                    >
                      {submittingCancel ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel Appointment"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Req #8: Ad-hoc meeting dialog */}
            <Dialog open={adhocDialogOpen} onOpenChange={setAdhocDialogOpen}>
              <DialogContent className="max-w-md" data-testid="adhoc-meeting-dialog">
                <DialogHeader>
                  <DialogTitle>Start Ad-Hoc Meeting</DialogTitle>
                  <DialogDescription>
                    Invite a facilitator or admin to an instant video meeting. They&apos;ll see a join prompt right away.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="adhoc-invitee">Invitee</Label>
                    <select
                      id="adhoc-invitee"
                      data-testid="adhoc-invitee-select"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={adhocInviteeId}
                      onChange={(e) => setAdhocInviteeId(e.target.value)}
                    >
                      <option value="">— Pick someone —</option>
                      {invitableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} {u.roles?.length ? `(${u.roles.join(", ")})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adhoc-topic">Topic <span className="text-xs text-muted-foreground">(optional)</span></Label>
                    <Textarea
                      id="adhoc-topic"
                      data-testid="adhoc-topic-input"
                      value={adhocTopic}
                      onChange={(e) => setAdhocTopic(e.target.value)}
                      placeholder="e.g. Quick chat about Mrs. Smith's eval results"
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setAdhocDialogOpen(false)}
                      className="flex-1"
                      disabled={adhocSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleStartAdhoc}
                      disabled={adhocSubmitting || !adhocInviteeId}
                      className="flex-1 gap-2"
                      data-testid="confirm-adhoc-meeting"
                    >
                      {adhocSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
                      Start Meeting
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Notification Settings Panel */}
        {showNotificationSettings && (
          <div className="mb-6">
            <NotificationSettings user={user} token={token} onUpdate={fetchData} />
          </div>
        )}

        {/* Profile Banner - only for therapists and admin_therapist */}
        {profile && canManageBlocks && (
          <Card className="bg-secondary/30">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Licensed in:</span>
                  <div className="flex flex-wrap gap-1">
                    {profile.states_covered?.length > 0 ? (
                      profile.states_covered.slice(0, 5).map((state) => (
                        <Badge key={state} variant="outline" className="text-xs">
                          {state}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No states configured</span>
                    )}
                    {profile.states_covered?.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{profile.states_covered.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProfileSettingsOpen(true)}
                  data-testid="edit-license-btn"
                >
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clickable Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "blocks" ? "ring-2 ring-emerald-500 bg-emerald-50" : ""}`}
            onClick={() => setActiveView("blocks")}
            data-testid="stat-blocks"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Blocks</p>
                  <p className="text-3xl font-bold mt-1">{activeBlocks.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Play className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "sessions" ? "ring-2 ring-primary bg-primary/5" : ""}`}
            onClick={() => setActiveView("sessions")}
            data-testid="stat-sessions"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Sessions</p>
                  <p className="text-3xl font-bold mt-1">{activeSessions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "queue" ? "ring-2 ring-amber-500 bg-amber-50" : ""}`}
            onClick={() => setActiveView("queue")}
            data-testid="stat-queue"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Queue</p>
                  <p className="text-3xl font-bold mt-1">{queue.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`card-hover cursor-pointer transition-all ${activeView === "history" ? "ring-2 ring-secondary bg-secondary/20" : ""}`}
            onClick={() => setActiveView("history")}
            data-testid="stat-completed"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed Today</p>
                  <p className="text-3xl font-bold mt-1">{completedSessions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-secondary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content based on selected stat card */}
        <div className="space-y-6">
          {/* Availability Blocks View */}
          {activeView === "blocks" && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">My Availability Calendar</CardTitle>
                  <Button onClick={() => setDialogOpen(true)} data-testid="add-availability-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Availability
                  </Button>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="text-sm text-muted-foreground">Availability</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500" />
                    <span className="text-sm text-muted-foreground">Appointments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-emerald-500" />
                    <span className="text-sm text-muted-foreground">Sessions</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Calendar Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button variant="outline" size="sm" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h3 className="font-semibold text-lg">
                    {format(calendarMonth, "MMMM yyyy")}
                  </h3>
                  <Button variant="outline" size="sm" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day headers */}
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {(() => {
                    const monthStart = startOfMonth(calendarMonth);
                    const monthEnd = endOfMonth(calendarMonth);
                    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                    const startDayOfWeek = monthStart.getDay();
                    
                    // Add empty cells for days before the month starts
                    const emptyCells = Array(startDayOfWeek).fill(null);
                    
                    return [...emptyCells, ...days].map((day, index) => {
                      if (!day) {
                        return <div key={`empty-${index}`} className="h-24 border rounded-md bg-muted/20" />;
                      }
                      
                      // Find blocks and appointments for this day
                      const dayBlocks = calendarData.availability_blocks?.filter((b) => {
                        const blockDate = new Date(b.start_time);
                        return isSameDay(blockDate, day);
                      }) || [];
                      
                      const dayAppointments = calendarData.appointments?.filter((a) => {
                        const apptDate = new Date(a.window_start);
                        return isSameDay(apptDate, day);
                      }) || [];
                      
                      const daySessions = calendarData.sessions?.filter((s) => {
                        const sessionDate = new Date(s.start_time);
                        return isSameDay(sessionDate, day);
                      }) || [];
                      
                      const isToday = isSameDay(day, new Date());
                      const isSelected = selectedCalendarDate && isSameDay(day, selectedCalendarDate);
                      
                      // Total items for "more" indicator
                      const totalItems = dayBlocks.length + dayAppointments.length + daySessions.length;
                      
                      return (
                        <div
                          key={day.toISOString()}
                          className={`h-24 border rounded-md p-1 cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden ${
                            isToday ? "border-primary border-2" : ""
                          } ${isSelected ? "bg-primary/10" : ""}`}
                          onClick={() => setSelectedCalendarDate(day)}
                        >
                          <div className={`text-sm font-medium mb-1 ${isToday ? "text-primary" : ""}`}>
                            {format(day, "d")}
                          </div>
                          <div className="space-y-0.5">
                            {/* Availability blocks in blue */}
                            {dayBlocks.slice(0, 1).map((block) => (
                              <div
                                key={block.id}
                                className="text-xs px-1 py-0.5 rounded bg-blue-500 text-white truncate"
                                title={`Availability: ${formatTime(block.start_time)} - ${formatTime(block.end_time)}`}
                              >
                                {formatTimeRange(block.start_time, block.end_time)}
                              </div>
                            ))}
                            {dayBlocks.length > 1 && (
                              <div className="text-xs text-blue-600">+{dayBlocks.length - 1} blocks</div>
                            )}
                            {/* Appointments in red */}
                            {dayAppointments.slice(0, 1).map((appt) => (
                              <div
                                key={appt.id}
                                className="text-xs px-1 py-0.5 rounded bg-red-500 text-white truncate"
                                title={`Appointment: ${appt.patient_name} at ${new Date(appt.window_start).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`}
                              >
                                {appt.patient_name?.split(" ")[0]}
                              </div>
                            ))}
                            {dayAppointments.length > 1 && (
                              <div className="text-xs text-red-600">+{dayAppointments.length - 1} appts</div>
                            )}
                            {/* Sessions in emerald */}
                            {daySessions.slice(0, 1).map((session) => {
                              const duration = getSessionDuration(session);
                              return (
                                <div
                                  key={session.id}
                                  className="text-xs px-1 py-0.5 rounded bg-emerald-500 text-white truncate"
                                  title={`Session: ${session.patient_name} - ${duration ? duration + ' min' : ''}`}
                                >
                                  {session.patient_name?.split(" ")[0]}{duration ? ` (${duration}m)` : ''}
                                </div>
                              );
                            })}
                            {totalItems > 3 && (
                              <div className="text-xs text-muted-foreground">
                                +{totalItems - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Selected Day Details */}
                {selectedCalendarDate && (
                  <Card className="mt-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">
                        {format(selectedCalendarDate, "EEEE, MMMM d, yyyy")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const dayBlocks = calendarData.availability_blocks?.filter((b) => {
                          const blockDate = new Date(b.start_time);
                          return isSameDay(blockDate, selectedCalendarDate);
                        }) || [];
                        
                        const dayAppointments = calendarData.appointments?.filter((a) => {
                          const apptDate = new Date(a.window_start);
                          return isSameDay(apptDate, selectedCalendarDate);
                        }) || [];
                        
                        const daySessions = calendarData.sessions?.filter((s) => {
                          const sessionDate = new Date(s.start_time);
                          return isSameDay(sessionDate, selectedCalendarDate);
                        }) || [];
                        
                        if (dayBlocks.length === 0 && dayAppointments.length === 0 && daySessions.length === 0) {
                          return (
                            <p className="text-sm text-muted-foreground">No availability, appointments, or sessions on this day</p>
                          );
                        }
                        
                        return (
                          <div className="space-y-4">
                            {dayBlocks.length > 0 && (
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <div className="w-3 h-3 rounded bg-blue-500" />
                                  Availability Blocks
                                </h4>
                                <div className="space-y-2">
                                  {dayBlocks.map((block) => (
                                    <div key={block.id} className="flex items-center justify-between p-2 border rounded-md bg-blue-50">
                                      <div>
                                        <p className="font-medium text-sm">
                                          {formatTime(block.start_time)} - {formatTime(block.end_time)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {block.specialty} • {block.states_covered?.join(", ")}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge className={`${blockStatusColors[block.status]} border text-xs`}>
                                          {block.status}
                                        </Badge>
                                        <Button size="sm" variant="ghost" onClick={() => handleEditBlock(block)}>
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteBlock(block.id)}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {dayAppointments.length > 0 && (
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <div className="w-3 h-3 rounded bg-red-500" />
                                  Scheduled Appointments
                                </h4>
                                <div className="space-y-2">
                                  {/* Sort appointments by start time (earliest first) */}
                                  {[...dayAppointments].sort((a, b) => new Date(a.window_start) - new Date(b.window_start)).map((appt) => (
                                    <div key={appt.id} className="flex items-center justify-between p-2 border rounded-md bg-red-50 gap-2">
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm">{appt.patient_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {new Date(appt.window_start).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - {new Date(appt.window_end).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} • {appt.visit_type} • {appt.required_discipline}
                                        </p>
                                        {appt.location && (
                                          <p className="text-xs text-blue-600 font-medium">
                                            Facility: {appt.location}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <Badge className={
                                          appt.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                                          appt.status === "checked_in" ? "bg-yellow-100 text-yellow-700" :
                                          appt.status === "ready" ? "bg-green-100 text-green-700" :
                                          "bg-gray-100 text-gray-700"
                                        }>
                                          {appt.status}
                                        </Badge>
                                        {/* Req #7: therapists can cancel scheduled / checked-in / ready appts */}
                                        {canManageBlocks && ["scheduled", "checked_in", "ready"].includes(appt.status) && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                            onClick={() => openCancelDialog(appt)}
                                            data-testid={`cancel-appt-${appt.id}`}
                                          >
                                            <X className="w-3 h-3 mr-1" />
                                            Cancel
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {daySessions.length > 0 && (
                              <div>
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                  <div className="w-3 h-3 rounded bg-emerald-500" />
                                  Completed Sessions
                                </h4>
                                <div className="space-y-2">
                                  {daySessions.map((session) => (
                                    <div key={session.id} className="flex items-center justify-between p-2 border rounded-md bg-emerald-50">
                                      <div>
                                        <p className="font-medium text-sm">{session.patient_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {session.start_time && formatTime(session.start_time)} • {session.outcome}
                                        </p>
                                      </div>
                                      <Button size="sm" variant="outline" onClick={() => navigate(`/session/${session.id}`)}>
                                        <Eye className="w-3 h-3 mr-1" />
                                        View
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}

          {/* Active Sessions View */}
          {activeView === "sessions" && (
            <div className="space-y-4">
            {activeSessions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No active sessions</h3>
                  <p className="text-muted-foreground">
                    Sessions will appear here when patients are matched to you
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {activeSessions.map((session) => (
                  <Card key={session.id} className="tracing-beam">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center relative">
                            <Activity className="w-6 h-6 text-primary" />
                            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 pulse-indicator" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{session.patient_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              Started: {formatTime(session.start_time)}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                Live Session
                              </Badge>
                              {session.daily_room_url && !session.video_call_ended && (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                  <Video className="w-3 h-3 mr-1" />
                                  Video Active
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/session/${session.id}`)}
                            data-testid={`video-session-${session.id}`}
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Video & Notes
                          </Button>
                          <Button
                            onClick={() => handleCompleteSession(session.id)}
                            data-testid={`complete-session-${session.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Complete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </div>
          )}

          {/* Queue View */}
          {activeView === "queue" && (
            <div id="patient-queue-section" className="space-y-4">
            {queue.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Queue is empty</h3>
                  <p className="text-muted-foreground">
                    Patients waiting for therapist matching will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-2">
                  <h3 className="font-semibold text-amber-800 mb-1">Ready to Start Session</h3>
                  <p className="text-sm text-amber-700">
                    Review patient details below and click "Start Session" to connect with the correct patient.
                  </p>
                </div>
                {queue.map((entry) => (
                  <Card key={entry.id} className="border-2 border-amber-200 bg-amber-50/30">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                            #{entry.position}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{entry.patient_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {entry.visit_type} • {entry.required_discipline}
                            </p>
                            {entry.location && (
                              <p className="text-sm text-blue-600 font-medium">
                                Facility: {entry.location}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">
                                <MapPin className="w-3 h-3 mr-1" />
                                {entry.state}
                              </Badge>
                              <Badge variant="outline">Priority: {entry.priority}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleStartSession(entry.id, entry.patient_name)}
                            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`start-session-${entry.id}`}
                          >
                            <Play className="w-4 h-4" />
                            Start Session
                          </Button>
                          {/* Admin can remove from queue */}
                          {isAdmin && (
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={() => handleRemoveFromQueue(entry.id, entry.patient_name)}
                              data-testid={`remove-queue-${entry.id}`}
                              title="Remove from queue"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </div>
          )}

          {/* Session History View */}
          {activeView === "history" && (
            <div className="space-y-4">
            {completedSessions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No completed sessions</h3>
                  <p className="text-muted-foreground">
                    Your completed sessions will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedSessions.map((session) => (
                  <Card key={session.id} className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{session.patient_name}</h3>
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {session.start_time ? new Date(session.start_time).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                              }) : 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.start_time ? new Date(session.start_time).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                              }) : 'N/A'}
                            </span>
                            {session.transcript && (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <FileText className="w-4 h-4" />
                                Transcript available
                              </span>
                            )}
                          </div>
                          {/* Notes Preview */}
                          {(session.pre_session_notes || session.during_session_notes || session.post_session_notes) && (
                            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm font-medium mb-1">Notes Preview:</p>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {session.post_session_notes || session.during_session_notes || session.pre_session_notes || 'No notes'}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/session/${session.id}`)}
                            className="gap-2"
                            data-testid={`view-session-${session.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/sessions/${session.id}/email-summary`);
                                if (res.data.success) {
                                  toast.success(res.data.message);
                                } else {
                                  toast.error(res.data.message);
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to send email");
                              }
                            }}
                            className="gap-2"
                            data-testid={`email-session-${session.id}`}
                          >
                            <Mail className="w-4 h-4" />
                            Email Summary
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </div>
          )}
        </div>

        {/* Recent ad-hoc meetings — rejoin a dropped call in one click. */}
        <RecentAdhocMeetings />
      </div>
      
      {/* Profile Settings Dialog - External control */}
      <ProfileSettingsDialog 
        profile={profile} 
        onSave={fetchData}
        externalOpen={profileSettingsOpen}
        onOpenChange={setProfileSettingsOpen}
      />
    </Layout>
  );
};

export default TherapistDashboard;
